import {
    Banner,
    BlockStack,
    Button,
    Card,
    InlineStack,
    Modal,
    ProgressBar,
    Select,
    Spinner,
    Text,
    TextField,
} from "@shopify/polaris";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import {
    useConfigureAnalytics,
    useCreateProperty,
    useGA4Accounts,
    useGA4DataStreams,
    useGA4Properties,
    useGoogleStatus,
    useProvisionAccount,
} from "../hooks/useAnalytics";
import { useSite } from "../hooks/useSites";
import { googleApi } from "../lib/api";

export function Setup() {
    const { siteId } = useParams<{ siteId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [selectedAccount, setSelectedAccount] = useState<string>("");
    const [selectedProperty, setSelectedProperty] = useState<string>("");
    const [selectedStream, setSelectedStream] = useState<string>("");
    const [step, setStep] = useState(1);
    const [isSearchingAccount, setIsSearchingAccount] = useState(false);
    const [searchAccountIndex, setSearchAccountIndex] = useState(0);
    const [hasCompletedSearch, setHasCompletedSearch] = useState(false);

    // Create Property modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newPropertyName, setNewPropertyName] = useState("");

    // Create Account modal state
    const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
    const [newAccountName, setNewAccountName] = useState("");

    const queryClient = useQueryClient();
    const { data: site, isLoading: siteLoading } = useSite(siteId!);
    const { data: googleStatus, isLoading: googleLoading } = useGoogleStatus(siteId!);
    const { data: accounts, isLoading: accountsLoading } = useGA4Accounts(
        siteId!,
        googleStatus?.connected ?? false
    );
    const { data: properties, isLoading: propertiesLoading } = useGA4Properties(
        siteId!,
        selectedAccount
    );
    const {
        data: dataStreams,
        isLoading: streamsLoading,
        isError: streamsError,
    } = useGA4DataStreams(siteId!, selectedProperty);
    const configureMutation = useConfigureAnalytics();
    const createPropertyMutation = useCreateProperty();
    const provisionAccountMutation = useProvisionAccount();

    const googleConnected = searchParams.get("google_connected") === "true";
    // Google redirects back with ?accountTicketId=xxx after ToS acceptance
    const accountProvisioned = searchParams.has("accountTicketId") || searchParams.has("accountId");

    // Pre-populate existing GA4 configuration - Step 1: Find the account containing the property
    useEffect(() => {
        if (
            accounts &&
            accounts.length > 0 &&
            site?.analytics?.property_id &&
            !selectedAccount &&
            !isSearchingAccount &&
            !hasCompletedSearch // Don't re-search if we already searched and didn't find
        ) {
            // Start searching for the account that contains the existing property
            setIsSearchingAccount(true);
            setSearchAccountIndex(0);
        }
    }, [
        accounts,
        site?.analytics?.property_id,
        selectedAccount,
        isSearchingAccount,
        hasCompletedSearch,
    ]);

    // Pre-populate - Step 2: Search through accounts to find the one with our property
    useEffect(() => {
        if (isSearchingAccount && accounts && searchAccountIndex < accounts.length) {
            // Set the account to trigger property fetch
            setSelectedAccount(accounts[searchAccountIndex].name);
        }
    }, [isSearchingAccount, accounts, searchAccountIndex]);

    // Pre-populate - Step 3: Check if current account has our property
    useEffect(() => {
        if (isSearchingAccount && properties && site?.analytics?.property_id) {
            const existingProperty = properties.find((p) => p.name === site.analytics?.property_id);

            if (existingProperty) {
                // Found the account! Keep this selection and set the property
                setSelectedProperty(existingProperty.name);
                setIsSearchingAccount(false);
                setHasCompletedSearch(true);
            } else if (accounts && searchAccountIndex < accounts.length - 1) {
                // Not found in this account, try the next one
                setSearchAccountIndex(searchAccountIndex + 1);
                setSelectedProperty("");
            } else {
                // Searched all accounts, property not found - stop searching
                // Don't reset selectedAccount to empty (causes infinite loop)
                // Just stop searching and let user pick manually
                setIsSearchingAccount(false);
                setHasCompletedSearch(true);
                // Keep the last account selected so user can choose a new property
            }
        }
    }, [
        isSearchingAccount,
        properties,
        site?.analytics?.property_id,
        accounts,
        searchAccountIndex,
    ]);

    // Pre-populate - Step 4: Select the data stream by matching name
    useEffect(() => {
        if (
            dataStreams &&
            dataStreams.length > 0 &&
            site?.analytics?.data_stream_name &&
            selectedProperty &&
            !selectedStream &&
            !isSearchingAccount
        ) {
            const existingStream = dataStreams.find(
                (s) => s.displayName === site.analytics?.data_stream_name
            );
            if (existingStream) {
                setSelectedStream(existingStream.name);
            }
        }
    }, [
        dataStreams,
        site?.analytics?.data_stream_name,
        selectedProperty,
        selectedStream,
        isSearchingAccount,
    ]);

    // Handle return from Google account provisioning - clean up URL params
    useEffect(() => {
        if (accountProvisioned) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("accountTicketId");
            newParams.delete("accountId");
            newParams.delete("account_provisioned");
            const newUrl = newParams.toString()
                ? `${window.location.pathname}?${newParams.toString()}`
                : window.location.pathname;
            window.history.replaceState({}, "", newUrl);
        }
    }, [accountProvisioned, searchParams]);

    // When returning from account provisioning, poll for new accounts
    // (Google's Admin API may not return the new account immediately)
    useEffect(() => {
        if (accountProvisioned && (!accounts || accounts.length === 0)) {
            let retryCount = 0;
            const maxRetries = 6;
            const interval = setInterval(() => {
                retryCount++;
                queryClient.invalidateQueries({ queryKey: ["ga4Accounts", siteId] });
                if (retryCount >= maxRetries) {
                    clearInterval(interval);
                }
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [accountProvisioned, accounts, siteId, queryClient]);

    // Auto-select account after provisioning if only one exists
    useEffect(() => {
        if (accountProvisioned && accounts && accounts.length > 0 && !selectedAccount) {
            setSelectedAccount(accounts[0].name);
        }
    }, [accountProvisioned, accounts, selectedAccount]);

    useEffect(() => {
        if (selectedAccount && !isSearchingAccount) setStep(2);
    }, [selectedAccount, isSearchingAccount]);

    useEffect(() => {
        if (selectedProperty && !isSearchingAccount) setStep(3);
    }, [selectedProperty, isSearchingAccount]);

    useEffect(() => {
        if (selectedStream && !isSearchingAccount) setStep(4);
    }, [selectedStream, isSearchingAccount]);

    if (siteLoading || googleLoading || isSearchingAccount) {
        return (
            <Layout>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "60px",
                        gap: "16px",
                    }}
                >
                    <Spinner size='large' />
                    {isSearchingAccount && (
                        <Text as='p' tone='subdued'>
                            Loading existing configuration...
                        </Text>
                    )}
                </div>
            </Layout>
        );
    }

    if (!googleStatus?.connected) {
        return (
            <Layout>
                <BlockStack gap='400'>
                    <Text as='h1' variant='headingLg' fontWeight='bold'>
                        Setup GA4
                    </Text>
                    <Card>
                        <BlockStack gap='400'>
                            <Text as='h2' variant='headingMd'>
                                Connect Google Account
                            </Text>
                            <Text as='p' tone='subdued'>
                                You need to connect your Google account to access GA4 properties.
                            </Text>
                            <Button
                                variant='primary'
                                onClick={() => {
                                    window.location.href = googleApi.getConnectUrl(siteId!);
                                }}
                            >
                                Connect Google Account
                            </Button>
                        </BlockStack>
                    </Card>
                </BlockStack>
            </Layout>
        );
    }

    const handleSave = async () => {
        const stream = dataStreams?.find((s) => s.name === selectedStream);
        if (!stream?.webStreamData) return;

        await configureMutation.mutateAsync({
            siteId: siteId!,
            config: {
                property_id: selectedProperty,
                measurement_id: stream.webStreamData.measurementId,
                data_stream_name: stream.displayName,
                web_stream_id: selectedStream,
            },
        });

        navigate(`/site/${siteId}`);
    };

    const accountOptions = [
        { label: "Select an account...", value: "" },
        ...(accounts?.map((a) => ({
            label: a.displayName,
            value: a.name,
        })) || []),
    ];

    const propertyOptions = [
        { label: "Select a property...", value: "" },
        ...(properties?.map((p) => ({
            label: p.displayName,
            value: p.name,
        })) || []),
    ];

    const streamOptions = [
        { label: "Select a data stream...", value: "" },
        ...(dataStreams?.map((s) => ({
            label: `${s.displayName} (${s.webStreamData?.measurementId || "N/A"})`,
            value: s.name,
        })) || []),
    ];

    return (
        <Layout>
            <BlockStack gap='400'>
                {/* Page Header */}
                <Text as='h1' variant='headingLg' fontWeight='bold'>
                    Setup GA4 for {site?.display_name}
                </Text>
                {googleConnected && (
                    <Banner tone='success' onDismiss={() => {}}>
                        Google account connected successfully! Now select your GA4 property.
                    </Banner>
                )}
                {accountProvisioned && accounts && accounts.length > 0 && (
                    <Banner tone='success' onDismiss={() => {}}>
                        GA4 account created successfully! Select it below to continue.
                    </Banner>
                )}
                {accountProvisioned && (!accounts || accounts.length === 0) && (
                    <Banner tone='info'>
                        <InlineStack gap='200' blockAlign='center'>
                            <Spinner size='small' />
                            <Text as='span'>
                                Looking for your new GA4 account... This may take a moment.
                            </Text>
                        </InlineStack>
                    </Banner>
                )}

                {/* Progress */}
                <Card>
                    <BlockStack gap='300'>
                        <Text as='p' variant='bodySm' tone='subdued'>
                            Step {step} of 4
                        </Text>
                        <ProgressBar progress={(step / 4) * 100} size='small' />
                    </BlockStack>
                </Card>

                {/* Step 1: Select Account */}
                <Card>
                    <BlockStack gap='400'>
                        <InlineStack align='space-between' blockAlign='center'>
                            <Text as='h2' variant='headingMd'>
                                1. Select GA4 Account
                            </Text>
                            {accountsLoading && <Spinner size='small' />}
                        </InlineStack>

                        {accounts?.length === 0 ? (
                            <BlockStack gap='300'>
                                <Banner tone='warning'>
                                    No GA4 accounts found. You can create a new Google Analytics
                                    account to get started.
                                </Banner>
                                {/* <Button
                  variant="primary"
                  onClick={() => {
                    setNewAccountName(site?.display_name || '');
                    setShowCreateAccountModal(true);
                  }}
                >
                  Create GA4 Account
                </Button> */}
                            </BlockStack>
                        ) : (
                            <Select
                                label='Account'
                                labelHidden
                                options={accountOptions}
                                value={selectedAccount}
                                onChange={(value) => {
                                    setSelectedAccount(value);
                                    setSelectedProperty("");
                                    setSelectedStream("");
                                }}
                                disabled={accountsLoading}
                            />
                        )}
                    </BlockStack>
                </Card>

                {/* Step 2: Select Property */}
                {selectedAccount && (
                    <Card>
                        <BlockStack gap='400'>
                            <InlineStack align='space-between' blockAlign='center'>
                                <Text as='h2' variant='headingMd'>
                                    2. Select GA4 Property
                                </Text>
                                {propertiesLoading && <Spinner size='small' />}
                            </InlineStack>

                            {properties?.length === 0 ? (
                                <BlockStack gap='300'>
                                    <Banner tone='warning'>
                                        No GA4 properties found in this account.
                                    </Banner>
                                    {/* <Button
                                        variant='primary'
                                        onClick={() => {
                                            setNewPropertyName(site?.display_name || "");
                                            setShowCreateModal(true);
                                        }}
                                    >
                                        Create New Property
                                    </Button> */}
                                </BlockStack>
                            ) : (
                                <BlockStack gap='300'>
                                    <Select
                                        label='Property'
                                        labelHidden
                                        options={propertyOptions}
                                        value={selectedProperty}
                                        onChange={(value) => {
                                            setSelectedProperty(value);
                                            setSelectedStream("");
                                        }}
                                        disabled={propertiesLoading}
                                    />
                                    {/* <Button
                                        variant='plain'
                                        onClick={() => {
                                            setNewPropertyName(site?.display_name || "");
                                            setShowCreateModal(true);
                                        }}
                                    >
                                        + Create New Property
                                    </Button> */}
                                </BlockStack>
                            )}
                        </BlockStack>
                    </Card>
                )}

                {/* Step 3: Select Data Stream */}
                {selectedProperty && (
                    <Card>
                        <BlockStack gap='400'>
                            <InlineStack align='space-between' blockAlign='center'>
                                <Text as='h2' variant='headingMd'>
                                    3. Select Web Data Stream
                                </Text>
                                {streamsLoading && <Spinner size='small' />}
                            </InlineStack>

                            {streamsError ? (
                                <Banner tone='critical'>
                                    Failed to load data streams. Please try reconnecting your Google
                                    account.
                                </Banner>
                            ) : dataStreams?.length === 0 ? (
                                <Banner tone='warning'>
                                    No web data streams found. Create a web data stream in Google
                                    Analytics first.
                                </Banner>
                            ) : (
                                <Select
                                    label='Data Stream'
                                    labelHidden
                                    options={streamOptions}
                                    value={selectedStream}
                                    onChange={setSelectedStream}
                                    disabled={streamsLoading}
                                />
                            )}
                        </BlockStack>
                    </Card>
                )}

                {/* Step 4: Save */}
                {selectedStream && (
                    <Card>
                        <BlockStack gap='400'>
                            <Text as='h2' variant='headingMd'>
                                4. Save Configuration
                            </Text>

                            {(() => {
                                const stream = dataStreams?.find((s) => s.name === selectedStream);
                                return stream?.webStreamData ? (
                                    <BlockStack gap='200'>
                                        <Text as='p' variant='bodySm'>
                                            Measurement ID:{" "}
                                            <strong>{stream.webStreamData.measurementId}</strong>
                                        </Text>
                                        <Text as='p' variant='bodySm'>
                                            Website:{" "}
                                            <strong>{`https://${site?.short_name}.webflow.io`}</strong>
                                        </Text>
                                    </BlockStack>
                                ) : null;
                            })()}

                            <InlineStack gap='200'>
                                <Button
                                    variant='primary'
                                    loading={configureMutation.isPending}
                                    onClick={handleSave}
                                >
                                    Save & Continue
                                </Button>
                                <Button onClick={() => navigate(`/site/${siteId}`)}>Cancel</Button>
                            </InlineStack>

                            {configureMutation.isError && (
                                <Banner tone='critical'>
                                    Failed to save configuration. Please try again.
                                </Banner>
                            )}
                        </BlockStack>
                    </Card>
                )}
            </BlockStack>

            {/* Create Property Modal */}
            <Modal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title='Create GA4 Property'
                primaryAction={{
                    content: "Create Property & Data Stream",
                    loading: createPropertyMutation.isPending,
                    onAction: async () => {
                        try {
                            const result = await createPropertyMutation.mutateAsync({
                                siteId: siteId!,
                                accountId: selectedAccount,
                                propertyName: newPropertyName || site?.display_name || "My Website",
                                websiteUrl: `https://${site?.short_name}.webflow.io`,
                            });

                            // Success - close modal and navigate to site page
                            setShowCreateModal(false);
                            setNewPropertyName("");

                            // Show success message and redirect
                            navigate(`/site/${siteId}`, {
                                state: {
                                    message: `GA4 property "${result.data.property_name}" created successfully! Measurement ID: ${result.data.measurement_id}`,
                                },
                            });
                        } catch (err) {
                            console.error("Failed to create property:", err);
                        }
                    },
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: () => setShowCreateModal(false),
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap='400'>
                        <TextField
                            label='Property Name'
                            value={newPropertyName}
                            onChange={setNewPropertyName}
                            placeholder={site?.display_name || "My Website"}
                            helpText='This will be the name shown in Google Analytics'
                            autoComplete='off'
                        />

                        <Text as='p' variant='bodySm' tone='subdued'>
                            This will create a new GA4 property and web data stream for your
                            website:
                        </Text>
                        <Text as='p' variant='bodySm'>
                            <strong>Website URL:</strong> {`https://${site?.short_name}.webflow.io`}
                        </Text>

                        {createPropertyMutation.isError && (
                            <Banner tone='critical'>
                                {createPropertyMutation.error instanceof Error
                                    ? createPropertyMutation.error.message
                                    : "Failed to create property. Please try again."}
                            </Banner>
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* Create Account Modal */}
            <Modal
                open={showCreateAccountModal}
                onClose={() => setShowCreateAccountModal(false)}
                title='Create GA4 Account'
                primaryAction={{
                    content: "Create Account",
                    loading: provisionAccountMutation.isPending,
                    onAction: async () => {
                        try {
                            const result = await provisionAccountMutation.mutateAsync({
                                siteId: siteId!,
                                displayName: newAccountName || site?.display_name || "My Website",
                            });

                            setShowCreateAccountModal(false);
                            setNewAccountName("");

                            // Store siteId so the provision-callback page can redirect back here
                            localStorage.setItem("provision_site_id", siteId!);

                            // Redirect to Google's provisioning/ToS page
                            window.location.href = result.data.provisioning_url;
                        } catch (err) {
                            console.error("Failed to provision account:", err);
                        }
                    },
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: () => setShowCreateAccountModal(false),
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap='400'>
                        <TextField
                            label='Account Name'
                            value={newAccountName}
                            onChange={setNewAccountName}
                            placeholder={site?.display_name || "My Website"}
                            helpText='This will be the name of your Google Analytics account'
                            autoComplete='off'
                        />

                        <Text as='p' variant='bodySm' tone='subdued'>
                            You&apos;ll be redirected to Google to accept Terms of Service and
                            complete account creation. After completing, you&apos;ll return here
                            automatically.
                        </Text>

                        {provisionAccountMutation.isError && (
                            <Banner tone='critical'>
                                {provisionAccountMutation.error instanceof Error
                                    ? provisionAccountMutation.error.message
                                    : "Failed to create account. Please try again."}
                            </Banner>
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </Layout>
    );
}
